import z from 'zod'
import { cn, objectEntries } from '@/helpers/utils'
import { OVERVIEW_CARD_CLASSNAME } from '@/pages/luxe-submissions-mm-data-overview'
import { type TKey, useTranslation } from '@/translations/i18n'
import type { MMDataOverviewInventoryBreakdown } from '../types'

const InventoryKeys = z.enum([
	'standardMpConversion',
	'standardNal',
	'brandedResidenceNal',
	'brandedResidenceMpConversion'
])
type InventoryKeys = z.infer<typeof InventoryKeys>

const INVENTORY_COLORS: Record<InventoryKeys, string> = {
	standardMpConversion: '#0086C9',
	standardNal: '#0BA5EC',
	brandedResidenceNal: '#36BFFA',
	brandedResidenceMpConversion: '#EAECF0'
}

const INVENTORY_LABEL_TKEYS = {
	standardMpConversion: 'luxe_submissions:inventory_standard_mp_conversion',
	standardNal: 'luxe_submissions:inventory_standard_nal',
	brandedResidenceNal: 'luxe_submissions:inventory_branded_residence_nal',
	brandedResidenceMpConversion:
		'luxe_submissions:inventory_branded_residence_mp_conversion'
} as const satisfies Record<InventoryKeys, TKey>
interface MMOverviewInventoryTypeProps {
	data: MMDataOverviewInventoryBreakdown
}

function DonutChart({
	data,
	total
}: {
	data: MMDataOverviewInventoryBreakdown
	total: number
}) {
	const size = 200
	const strokeWidth = 40
	const radius = (size - strokeWidth) / 2
	const circumference = 2 * Math.PI * radius
	const center = size / 2

	let cumulativeOffset = 0

	return (
		<svg
			className='shrink-0'
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			width={size}
		>
			<title>Inventory Type Distribution</title>
			{objectEntries(data).map(([item, count]) => {
				const ratio = total > 0 ? count / total : 0
				const dashLength = ratio * circumference
				const dashOffset = -cumulativeOffset
				cumulativeOffset += dashLength

				return (
					<circle
						cx={center}
						cy={center}
						fill='none'
						key={item}
						r={radius}
						stroke={INVENTORY_COLORS[item]}
						strokeDasharray={`${dashLength} ${circumference - dashLength}`}
						strokeDashoffset={dashOffset}
						strokeWidth={strokeWidth}
						transform={`rotate(-90 ${center} ${center})`}
					/>
				)
			})}
		</svg>
	)
}

function MMOverviewInventoryType({ data }: MMOverviewInventoryTypeProps) {
	const { t } = useTranslation()
	const total = InventoryKeys.options.reduce((sum, key) => sum + data[key], 0)

	return (
		<div className={cn(OVERVIEW_CARD_CLASSNAME, 'gap-6 px-8')}>
			<span className='font-semibold text-grey-1000 text-lg'>
				{t('luxe_submissions:by_inventory_type')}
			</span>

			<div className='flex items-center gap-8'>
				<div className='relative'>
					<DonutChart data={data} total={total} />
					<div className='absolute inset-0 flex flex-col items-center justify-center'>
						<span className='font-semibold text-2xl text-grey-1000'>
							{total}
						</span>
						<span className='text-grey-1000 text-sm'>
							{t('luxe_submissions:total_listings', {
								count: total
							})}
						</span>
					</div>
				</div>

				<div className='flex flex-col gap-4'>
					{objectEntries(data).map(([item, count]) => (
						<div
							className='flex items-center gap-2 text-neutral-600 text-sm'
							key={item}
						>
							<span
								className='size-2 shrink-0 rounded-full'
								style={{
									backgroundColor: INVENTORY_COLORS[item]
								}}
							/>
							<span className='font-bold'>
								{t(INVENTORY_LABEL_TKEYS[item])}
							</span>
							<span>
								· {count}{' '}
								{t('luxe_submissions:listing', {
									count
								}).toLowerCase()}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export { MMOverviewInventoryType }
